'use client';

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from 'next/image';
import Link from 'next/link';
import { Eye, EyeOff } from 'lucide-react';
import PasswordGuide from '@/app/components/passwordGuide';
import sdeMarketplaceLogo from '@/public/sdemarketplaceLogo2.png';

function SignupPageContent() {
  const searchParams = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const fromGoogle = searchParams.get("from") === "google";
  const [fullname, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState(emailParam);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTnC, setAcceptedTnC] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const router = useRouter();

  // Status message for errors
  const [errors, setErrors] = useState({
    fullname: "",
    phoneNumber: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    terms: "",
  });

  // Store password in sessionStorage
  useEffect(() => {
    if (password) {
      sessionStorage.setItem("signup_password", password);
    }
  }, [password]);

  // This useEffect hook scrolls to the first field with an error.
  useEffect(() => {
    const fieldOrder: (keyof typeof errors)[] = [
      'fullname',
      'phoneNumber',
      'username',
      'email',
      'password',
      'confirmPassword',
      'terms'
    ];
    const firstErrorField = fieldOrder.find(field => errors[field]);
    if (firstErrorField) {
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errors]);

  // This useEffect hook runs once on component mount to safely load and then clear data.
  useEffect(() => {
    if (sessionStorage.getItem("signup_state_was_saved") === "true") {
      setFullName(sessionStorage.getItem("signup_fullname") || "");
      setPhoneNumber(sessionStorage.getItem("signup_phoneNumber") || "");
      if (!emailParam) {
        setEmail(sessionStorage.getItem("signup_email") || "");
      }
      setUsername(sessionStorage.getItem("signup_username") || "");
      setPassword(sessionStorage.getItem("signup_password") || "");
      setConfirmPassword(sessionStorage.getItem("signup_confirmPassword") || "");
      setAcceptedTnC(JSON.parse(sessionStorage.getItem("signup_acceptedTnC") || "false"));
      sessionStorage.removeItem("signup_fullname");
      sessionStorage.removeItem("signup_phoneNumber");
      sessionStorage.removeItem("signup_email");
      sessionStorage.removeItem("signup_username");
      sessionStorage.removeItem("signup_password");
      sessionStorage.removeItem("signup_confirmPassword");
      sessionStorage.removeItem("signup_acceptedTnC");
      sessionStorage.removeItem("signup_state_was_saved");
    }
  }, [emailParam]);

  const saveFormStateToSession = () => {
    sessionStorage.setItem("signup_fullname", fullname);
    sessionStorage.setItem("signup_phoneNumber", phoneNumber);
    sessionStorage.setItem("signup_email", email);
    sessionStorage.setItem("signup_username", username);
    sessionStorage.setItem("signup_password", password);
    sessionStorage.setItem("signup_confirmPassword", confirmPassword);
    sessionStorage.setItem("signup_acceptedTnC", JSON.stringify(acceptedTnC));
    // We add a flag to know that we intentionally saved the state.
    sessionStorage.setItem("signup_state_was_saved", "true");
  };

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, '');
    const limitedValue = digitsOnly.slice(0, 12);
    setPhoneNumber(limitedValue);
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors = {
      fullname: "",
      phoneNumber: "",
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
      terms: "",
    };

    let isValid = true;

    // Full name
    if (!fullname.trim()) {
      newErrors.fullname = "Full name is required.";
      isValid = false;
    } else if (!/^[A-Z\s.'-/@]+$/.test(fullname)) {
      newErrors.fullname = "Full name can only contain letters, spaces, and characters .'-/@.";
      isValid = false;
    } else if (fullname.trim().length < 3) {
      newErrors.fullname = "Full name must be at least 3 characters long.";
      isValid = false;
    }

    // Phone
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required.";
      isValid = false;
    } else if (!/^6\d{10,11}$/.test(phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be 11 or 12 digits and start with 6.';
      isValid = false;
    }

    // Email
    if (!email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email format.";
      isValid = false;
    }

    // Username
    if (!username.trim()) {
      newErrors.username = "Username is required.";
      isValid = false;
    } else {
      const usernameFormatRegex = /^[a-zA-Z0-9_]{3,20}$/;
      if (!usernameFormatRegex.test(username)) {
        newErrors.username = 'Username must be 3-20 characters long (letters, numbers, underscores only).';
        isValid = false;
      } else {
        const hasLetter = /[a-zA-Z]/.test(username);
        const hasNumber = /\d/.test(username);
        const hasUnderscore = /_/.test(username);

        if (!hasLetter || !hasNumber || !hasUnderscore) {
          newErrors.username = 'Username must include a combination of letters, numbers, and an underscore (_).';
          isValid = false;
        }
      }
    }

    // Password
    if (!password.trim()) {
      newErrors.password = "Password is required.";
      isValid = false;
    } else {
      const isValidPassword =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(password);
      if (!isValidPassword) {
        newErrors.password = "Password does not meet the required guidelines.";
        isValid = false;
      }
    }
    // Confirm Password
    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    // Terms
    if (!acceptedTnC) {
      newErrors.terms = "You must accept Terms & Conditions and Privacy Policy.";
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) return;

    // Proceed to API if valid
    const res = await fetch("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({
        fullname,
        phoneNumber,
        email,
        username,
        password,
        acceptTnC: true,
        acceptAt: new Date().toISOString(),
      }),
    });

    const data = await res.json();
    if (res.ok) {
      // Clear sessionStorage on successful signup
      sessionStorage.removeItem("signup_fullname");
      sessionStorage.removeItem("signup_phoneNumber");
      sessionStorage.removeItem("signup_email");
      sessionStorage.removeItem("signup_username");
      // sessionStorage.removeItem("signup_password");
      sessionStorage.removeItem("signup_confirmPassword");
      sessionStorage.removeItem("signup_acceptedTnC");

      router.push("/verify?email=" + email);
    } else {
      // Handle specific API errors
      const newErrors = {
        fullname: "",
        phoneNumber: "",
        email: "",
        username: "",
        password: "",
        confirmPassword: "",
        terms: "",
      };
      if (data.error === "Email already registered.") {
        newErrors.email = data.error;
      } else if (data.error === "Username already taken.") {
        newErrors.username = data.error;
      } else {
        // Fallback for unknown errors
        newErrors.email = data.error || "Signup failed.";
      }

      setErrors(newErrors);
    }
  };

  return (
    <section className="bg-slate-50 flex items-center justify-center min-h-screen py-10 md:py-16 px-4">
      <div className="w-full max-w-lg mx-auto p-6 md:p-12 bg-white rounded-2xl border border-gray-200 shadow-lg">

        {/* 1. logo */}
        <div className="flex justify-center mb-8 md:mb-10">
          <Link href="/">
            <Image
              src={sdeMarketplaceLogo}
              alt="SDeMARKETPLACE Logo"
              width={220}
              height={50}
              priority
              className="w-44 md:w-56 h-auto"
            />
          </Link>
        </div>

        {/* 2. Form */}
        <form onSubmit={handleSignup} className="space-y-5 md:space-y-6">

          {/* Full Name Input */}
          <div>
            <label htmlFor="fullname" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">Full Name<span className="text-red-500">*</span></label>
            <input id="fullname" type="text" placeholder="Enter your full name"
              className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
              ${errors.fullname ? "border-red-500" : "border-gray-200"}`}
              value={fullname} onChange={(e) => setFullName(e.target.value.toUpperCase())} suppressHydrationWarning />
            {errors.fullname && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.fullname}</p>}
          </div>

          {/* Phone Number Input */}
          <div>
            <label htmlFor="phoneNumber" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">Phone Number<span className="text-red-500">*</span></label>
            <input id="phoneNumber" type="text" placeholder="e.g., 60123456789" inputMode="numeric"
              className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
              ${errors.phoneNumber ? "border-red-500" : "border-gray-200"}`}
              value={phoneNumber} onChange={handlePhoneNumberChange} suppressHydrationWarning />
            {errors.phoneNumber && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.phoneNumber}</p>}
          </div>

          {/* Username Input */}
          <div>
            <label htmlFor="username" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">Username<span className="text-red-500">*</span></label>
            <input id="username" type="text" placeholder="Enter your username"
              className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
              ${errors.username ? "border-red-500" : "border-gray-200"}`}
              value={username} onChange={(e) => setUsername(e.target.value)} suppressHydrationWarning />
            {errors.username && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.username}</p>}
          </div>

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">Email<span className="text-red-500">*</span></label>
            <input id="email" type="text" placeholder="e.g., user@gmail.com"
              className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
              ${errors.email ? "border-red-500" : "border-gray-200"}`}
              value={email} onChange={(e) => setEmail(e.target.value)} suppressHydrationWarning disabled={fromGoogle} />
            {errors.email && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">Password<span className="text-red-500">*</span></label>
            <div className="relative">
              <input id="password" type={showPassword ? "text" : "password"} placeholder="Enter your password"
                className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
                ${errors.password ? "border-red-500" : "border-gray-200"}`}
                value={password} onChange={(e) => setPassword(e.target.value)} suppressHydrationWarning />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 md:pr-4 text-slate-400 hover:text-indigo-600"
                suppressHydrationWarning>
                {showPassword ? (
                  <EyeOff className="h-4 w-4 md:h-5 md:w-5" />
                ) : (
                  <Eye className="h-4 w-4 md:h-5 md:w-5" />
                )}
              </button>
            </div>
            {errors.password && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.password}</p>}
          </div>

          {/* Confirm Password Input */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">Confirm Password<span className="text-red-500">*</span></label>
            <div className="relative">
              <input id="confirmPassword" type={showConfirmPassword ? "text" : "password"} placeholder="Re-enter your password"
                className={`w-full px-4 py-2 md:py-3 bg-white rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
                ${errors.confirmPassword ? "border-red-500" : "border-gray-200"}`}
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} suppressHydrationWarning />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 md:pr-4 text-slate-400 hover:text-indigo-600"
                suppressHydrationWarning>
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 md:h-5 md:w-5" />
                ) : (
                  <Eye className="h-4 w-4 md:h-5 md:w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-600 text-xs md:text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Password Guide */}
          {password && <PasswordGuide password_confirmation={password} />}

          {/* Terms and Conditions */}
          <div className="flex flex-col">
            <div className="flex items-center">
              <input
                id="terms"
                type="checkbox"
                checked={acceptedTnC}
                onChange={(e) => setAcceptedTnC(e.target.checked)}
                className="h-3 w-3 md:h-4 md:w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
              />
              <label
                htmlFor="terms"
                className="ml-3 text-xs md:text-sm text-slate-600 leading-tight"
              >
                I accept the{" "}
                <Link
                  href="/terms-and-conditions"
                  className="font-semibold text-indigo-700 hover:underline"
                  onClick={saveFormStateToSession}
                >
                  Terms & Conditions
                </Link>{" "}
                and{" "}
                <Link
                  href="/privacy-policy"
                  className="font-semibold text-indigo-700 hover:underline"
                  onClick={saveFormStateToSession}
                >
                  Privacy Policy
                </Link>
                .
              </label>
            </div>
            {errors.terms && (
              <p className="text-red-600 text-xs md:text-sm mt-1">{errors.terms}</p>
            )}
          </div>

          {/* Sign Up Button */}
          <div>
            <button type="submit"
              className="w-full bg-[#1e2d4d] text-white font-semibold py-2.5 md:py-3 px-6 rounded-lg transition-colors duration-200 hover:bg-indigo-700 text-sm md:text-base"
              suppressHydrationWarning>
              Sign Up
            </button>
          </div>
        </form>

        {/* Sign In Link */}
        <div className="text-center mt-4">
          <p className="text-xs md:text-sm text-slate-600">
            Already have an account?{" "}
            <a href="/login" className="text-indigo-700 hover:underline font-semibold hover:cursor-pointer">
              Sign In
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SignupPageContent />
    </Suspense>
  );
}